// Inheritance-mode-aware cascade priors (pure). Given the family's per-disease
// inheritance pattern (from the analyzer) and a relative's relationship to an
// index, return the carrier/at-risk prior, the derivation behind it, and a band.
//
// ⚠️ The prior values are clinical decisions — review against standard cascade
// references. Conservative by design: anything that can't transmit → prior 0,
// and anything whose transmission math we have NOT implemented → no prior at
// all (see MODE HONESTY below).
//
// IMPLEMENTED MODES
//  - Dominant (AD, and the no-mode-asserted fallback): degree ladder
//    0.5 / 0.25 / 0.125 to 3rd degree.
//  - Autosomal recessive: first-degree of an AFFECTED index — parents & children
//    are obligate carriers (band 'obligate'), unaffected full sibs 2/3,
//    half-sibs 1/2 (one obligate-carrier parent, transmitted at ½); a
//    carrier-only index transmits at 0.5 to first-degree; beyond that → 0
//    (AR cascade is reproductive/first-degree focused).
//
// MODE HONESTY (TGCLOUD-1712 W4.1, decision DR-4)
//
// XLD / XLR / MT / YL used to fall back to the DOMINANT DEGREE LADDER. That is
// not a rough approximation, it is the wrong answer with a confident-looking
// number on it: under X-linked recessive the son of an affected father has a
// carrier/affected prior of ZERO (he takes his father's Y), and the ladder said
// 0.5. Mitochondrial inheritance is maternal-only — every prior through a father
// is 0 — and Y-linked is paternal-line males only.
//
// So where the DISPLAYED mode's transmission math is not implemented, this
// module emits NO PRIOR: `prior: null`, band 'unavailable', and a derivation
// carrying the reason. The UI shows the relationship and the mode and says no
// prior is available. An honest absence beats a wrong number.
//
// Sex-aware transmission math is deliberately NOT implemented here. It needs
// sex-path traversal plus its own clinical validation set, and half-built
// sex-awareness is worse than none: it would look authoritative.

import { PATTERN, CONFIDENCE, PATTERN_MODE_LABEL } from './constants';

export const CASCADE_DEPTH_CAP = 3;

const DOMINANT_LADDER = { 1: 0.5, 2: 0.25, 3: 0.125 };
const LADDER_FRACTION = {
  1: { numerator: 1, denominator: 2 },
  2: { numerator: 1, denominator: 4 },
  3: { numerator: 1, denominator: 8 }
};
const AR_SIB_CARRIER = 2 / 3; // unaffected full sib of an AR-affected individual

// How the number was arrived at — recorded on every candidate so the cascade
// panel can state what priced it and a reviewer can tell the three apart.
export const PRICING_BASIS = Object.freeze({
  // The displayed mode's own transmission math.
  modeSpecific: 'mode_specific',
  // The generic per-degree ladder: no mode is asserted, or one is asserted but
  // not strongly enough to price with (see STRONG-ONLY GATE).
  degreeLadder: 'degree_ladder',
  // A mode IS displayed and we cannot price under it.
  unsupportedMode: 'unsupported_mode'
});

export const NO_PRIOR_REASON = Object.freeze({
  // The displayed mode's transmission math is not implemented (W4.1 / DR-4).
  transmissionMathNotImplemented: 'transmission_math_not_implemented',
  // The individual's own result for the gene is already known, so a prior for
  // them is meaningless (W4.3). Assigned by detectors/cascade.js.
  resultAlreadyKnown: 'result_already_known'
});

// Modes whose transmission math this module implements.
const SUPPORTED_MODES = Object.freeze(new Set([PATTERN.AD, PATTERN.AR]));

// Modes we can DISPLAY but cannot price. Every one is sex-path or lineage
// dependent, which is precisely what the degree ladder cannot express.
export const UNPRICEABLE_MODES = Object.freeze(
  new Set([PATTERN.XLD, PATTERN.XLR, PATTERN.MT, PATTERN.YL])
);

// English source strings; translated at render, as everywhere else in this
// analyzer (cf. detectors/alerts.js `detail`, inheritance `rationale`).
// TGCLOUD-1669: this used to be a local mirror of the panel's PATTERN_LABEL.
// Both now read the shared map in ./constants, as the old comment here asked.
const MODE_LABEL = PATTERN_MODE_LABEL;

const RELATIONSHIP_LABEL = {
  parent: 'parent',
  child: 'child',
  fullSib: 'full sibling',
  halfSib: 'half-sibling',
  grandparent: 'grandparent',
  grandchild: 'grandchild',
  auntUncle: 'aunt/uncle',
  halfAuntUncle: 'half-aunt/uncle',
  nieceNephew: 'niece/nephew',
  halfNieceNephew: 'half-niece/nephew',
  firstCousin: 'first cousin',
  greatGrandparent: 'great-grandparent',
  greatGrandchild: 'great-grandchild',
  greatAuntUncle: 'great-aunt/uncle',
  grandNieceNephew: 'grand-niece/nephew'
};

const DEGREE_LABEL = {
  1: 'first-degree',
  2: 'second-degree',
  3: 'third-degree'
};

export const modeLabel = (mode) => (mode && MODE_LABEL[mode]) || null;
export const relationshipLabel = (kind) =>
  RELATIONSHIP_LABEL[kind] || 'relative';

/**
 * A prior as a plain "1 in N" fraction. Emitted alongside the exact decimal so
 * the UI never has to derive one by rounding (TGCLOUD-1712 W4.9: `Math.round`
 * turned 12.5% into "13%", a value no genetics reference produces).
 */
function fractionLabel(fraction) {
  if (!fraction) return null;
  const { numerator, denominator } = fraction;
  if (denominator === 1) return 'certain';
  return `${numerator} in ${denominator}`;
}

// ---------------------------------------------------------------------------
// STRONG-ONLY GATE (TGCLOUD-1712 W4.2)
// ---------------------------------------------------------------------------
//
// Mode-specific math is used only when the pattern is STRONGLY supported. That
// gate is retained, but it used to be silent and invisible: the panel above the
// cascade displays the best pattern at ANY confidence, so a "moderate X-linked
// recessive" heading sat directly on top of dominant-ladder numbers with nothing
// recording the disagreement. The decision is now returned as data
// (`strongOnlyGateApplied`, `modeDivergence`) and travels on every candidate, so
// the two panels cannot contradict each other unnoticed.
//
// NOTE the deliberate asymmetry: the gate applies to PRICING, not to the
// unpriceable-mode rule. A moderately-supported X-linked pattern still suppresses
// the prior — the harm in W4.1 is a wrong number under a displayed mode, and the
// displayed mode is displayed at any confidence.

/**
 * The pricing decision for one displayed inheritance pattern.
 *
 * @param {{ id: string, confidence: string, ruledOut?: boolean }|null} pattern
 *   the analyzer's best pattern for the disease — i.e. what the panel above the
 *   cascade DISPLAYS. `confidence` is still read (decision DR-2 keeps the field
 *   while replacing the grade with evidence statements).
 * @returns {{ displayedMode: string|null, displayedConfidence: string|null,
 *             pricedMode: string|null, basis: string, supported: boolean,
 *             unsupportedReason: string|null, strongOnlyGateApplied: boolean,
 *             modeDivergence: boolean }}
 */
export function resolveCascadeMode(pattern) {
  const ruledOut = !!(pattern && pattern.ruledOut);
  // A ruled-out pattern is not displayed as the family's mode, so it neither
  // prices nor suppresses.
  const displayedMode = pattern && !ruledOut ? pattern.id || null : null;
  const displayedConfidence = pattern ? pattern.confidence || null : null;

  if (!displayedMode) {
    return {
      displayedMode: null,
      displayedConfidence,
      pricedMode: null,
      basis: PRICING_BASIS.degreeLadder,
      supported: true,
      unsupportedReason: null,
      strongOnlyGateApplied: false,
      modeDivergence: false
    };
  }

  if (UNPRICEABLE_MODES.has(displayedMode)) {
    return {
      displayedMode,
      displayedConfidence,
      pricedMode: null,
      basis: PRICING_BASIS.unsupportedMode,
      supported: false,
      unsupportedReason: NO_PRIOR_REASON.transmissionMathNotImplemented,
      strongOnlyGateApplied: false,
      // Nothing to diverge from: no number is emitted at all.
      modeDivergence: false
    };
  }

  const strong = displayedConfidence === CONFIDENCE.strong;
  const pricedMode =
    strong && SUPPORTED_MODES.has(displayedMode) ? displayedMode : null;
  return {
    displayedMode,
    displayedConfidence,
    pricedMode,
    basis: pricedMode ? PRICING_BASIS.modeSpecific : PRICING_BASIS.degreeLadder,
    supported: true,
    unsupportedReason: null,
    strongOnlyGateApplied: !pricedMode,
    // The mode on display is not the mode that priced this. For AD the ladder is
    // numerically identical, so this is informational there; for AR it is a real
    // numeric difference (2/3 and obligate parents vs a flat 0.5).
    modeDivergence: !pricedMode
  };
}

/**
 * Accepts either a pricing decision (from resolveCascadeMode) or a bare
 * PATTERN id / null, and always returns a pricing decision.
 *
 * Tolerating a bare id keeps a caller that passes `PATTERN.XLR` directly on the
 * SAFE side of W4.1 — it gets no prior, not a dominant-ladder one.
 */
export function pricingForMode(modeOrPricing) {
  if (modeOrPricing && typeof modeOrPricing === 'object')
    return modeOrPricing.basis
      ? modeOrPricing
      : resolveCascadeMode(modeOrPricing);
  if (!modeOrPricing) return resolveCascadeMode(null);
  return resolveCascadeMode({
    id: modeOrPricing,
    confidence: CONFIDENCE.strong,
    ruledOut: false
  });
}

// The index this relative is being priced against, in words.
const indexDescriptor = (indexAffected) =>
  indexAffected ? 'an affected individual' : 'a confirmed carrier';

function derivation({
  kind,
  rule,
  rel,
  pricing,
  fraction,
  assumesUnknownIntermediate,
  summary
}) {
  return {
    // 'deduction' = a pedigree fact (obligate carrier). 'probability' = a prior.
    // 'unavailable' = we decline to state one.
    kind,
    rule,
    relationship: rel.kind || null,
    relationshipLabel: relationshipLabel(rel.kind),
    degree: rel.degree ?? null,
    degreeLabel: DEGREE_LABEL[rel.degree] || null,
    indexAffected: !!rel.indexAffected,
    mode: pricing.displayedMode,
    modeLabel: modeLabel(pricing.displayedMode),
    pricedMode: pricing.pricedMode,
    basis: pricing.basis,
    fraction: fraction || null,
    fractionLabel: fractionLabel(fraction),
    // True when the number assumes the genotype of an intermediate relative we
    // have not tested. Consumed by the candidate's `isConditional` qualifier.
    assumesUnknownIntermediate: !!assumesUnknownIntermediate,
    // One inline sentence, information not instruction (decision DR-6).
    summary
  };
}

function unavailablePrior(rel, pricing) {
  const label = relationshipLabel(rel.kind);
  const mode = modeLabel(pricing.displayedMode) || 'the displayed mode';
  return {
    prior: null,
    band: 'unavailable',
    derivation: derivation({
      kind: 'unavailable',
      rule: 'unsupported_mode',
      rel,
      pricing,
      fraction: null,
      assumesUnknownIntermediate: false,
      summary:
        `${label} of ${indexDescriptor(
          rel.indexAffected
        )}, ${mode} — no prior ` +
        'stated: transmission under this mode depends on sex and lineage, which ' +
        'this analysis does not model'
    })
  };
}

function dominantPrior(rel, pricing) {
  const prior = DOMINANT_LADDER[rel.degree] ?? 0;
  const fraction = LADDER_FRACTION[rel.degree] || null;
  if (!(prior > 0)) {
    return {
      prior: 0,
      band: 'none',
      derivation: derivation({
        kind: 'probability',
        rule: 'beyond_depth_cap',
        rel,
        pricing,
        fraction: null,
        assumesUnknownIntermediate: false,
        summary: `beyond ${CASCADE_DEPTH_CAP} degrees of relationship — not priced`
      })
    };
  }
  const degreeLabel = DEGREE_LABEL[rel.degree] || `${rel.degree}-degree`;
  const mode = modeLabel(pricing.displayedMode);
  // Name the ladder when no mode priced it, so "1 in 4" is never read as the
  // displayed mode's own arithmetic.
  const modePhrase = pricing.pricedMode
    ? `${mode}`
    : mode
    ? `${mode} displayed; priced by the general degree relationship`
    : 'no inheritance mode asserted; priced by the general degree relationship';
  return {
    prior,
    band: 'probabilistic',
    derivation: derivation({
      kind: 'probability',
      rule: pricing.pricedMode ? 'dominant_degree_ladder' : 'degree_ladder',
      rel,
      pricing,
      fraction,
      assumesUnknownIntermediate: rel.degree > 1,
      summary:
        `${degreeLabel} relative (${relationshipLabel(rel.kind)}) of ` +
        `${indexDescriptor(rel.indexAffected)}, ${modePhrase} — ` +
        `${fractionLabel(fraction)}`
    })
  };
}

// rel: { kind, degree, indexAffected }
function arPrior(rel, pricing) {
  const label = relationshipLabel(rel.kind);
  const of = indexDescriptor(rel.indexAffected);

  if (rel.indexAffected) {
    // Obligate heterozygotes: an affected person's parents each transmitted an
    // allele, and each child received one. A pedigree DEDUCTION, not a prior.
    if (rel.kind === 'parent' || rel.kind === 'child') {
      return {
        prior: 1,
        band: 'obligate',
        derivation: derivation({
          kind: 'deduction',
          rule: 'ar_obligate_first_degree',
          rel,
          pricing,
          fraction: { numerator: 1, denominator: 1 },
          assumesUnknownIntermediate: false,
          summary:
            `${label} of ${of}, autosomal recessive — obligate carrier: an ` +
            'affected individual inherited one variant allele from each parent ' +
            'and passes one to each child'
        })
      };
    }
    if (rel.kind === 'fullSib') {
      return {
        prior: AR_SIB_CARRIER,
        band: 'probabilistic',
        derivation: derivation({
          kind: 'probability',
          rule: 'ar_unaffected_full_sib',
          rel,
          pricing,
          fraction: { numerator: 2, denominator: 3 },
          assumesUnknownIntermediate: false,
          summary:
            `unaffected ${label} of ${of}, autosomal recessive — 2 in 3 ` +
            '(both parents are obligate carriers; being unaffected excludes the ' +
            'homozygous quarter)'
        })
      };
    }
    // TGCLOUD-1712 CT-1. A half-sibling shares ONE parent, who is an obligate
    // carrier, and inherits that single allele at ½. Keyed on `kind` BEFORE the
    // degree test below on purpose: half-sibs carry degree 2 (they share ~25% of
    // their genome), and the first-degree-only rule would otherwise price this
    // real AR risk at zero and drop them from the cascade entirely.
    if (rel.kind === 'halfSib') {
      return {
        prior: 0.5,
        band: 'probabilistic',
        derivation: derivation({
          kind: 'probability',
          rule: 'ar_half_sib_of_affected',
          rel,
          pricing,
          fraction: { numerator: 1, denominator: 2 },
          // The shared parent's carrier state is DEDUCED (obligate), not assumed.
          assumesUnknownIntermediate: false,
          summary:
            `${label} of ${of}, autosomal recessive — 1 in 2 (the shared parent ` +
            'is an obligate carrier and transmits the allele half the time)'
        })
      };
    }
  }

  if (rel.degree === 1) {
    // Carrier-only index transmits its single allele at 0.5 to first-degree.
    return {
      prior: 0.5,
      band: 'probabilistic',
      derivation: derivation({
        kind: 'probability',
        rule: 'ar_carrier_index_first_degree',
        rel,
        pricing,
        fraction: { numerator: 1, denominator: 2 },
        assumesUnknownIntermediate: false,
        summary:
          `${label} of ${of}, autosomal recessive — 1 in 2 (a heterozygous ` +
          'carrier transmits the variant allele half the time)'
      })
    };
  }

  // AR cascade is first-degree focused; beyond that → not a v1 candidate.
  return {
    prior: 0,
    band: 'none',
    derivation: derivation({
      kind: 'probability',
      rule: 'ar_beyond_first_degree',
      rel,
      pricing,
      fraction: null,
      assumesUnknownIntermediate: false,
      summary:
        `${label} of ${of}, autosomal recessive — outside the first-degree ` +
        'cascade this analysis covers'
    })
  };
}

/**
 * The prior for one relative, with its derivation.
 *
 * @param {object|string|null} modeOrPricing a resolveCascadeMode() decision, or
 *   a bare PATTERN id / null.
 * @param {{ kind: string, degree: number, indexAffected?: boolean }} rel
 * @returns {{ prior: number|null, band: string, derivation: object }}
 *   `prior` is the EXACT value (2/3 stays 2/3, 0.125 stays 0.125) — formatting
 *   is the UI's job. `null` means no prior is available; see MODE HONESTY.
 */
export function cascadePrior(modeOrPricing, rel) {
  const pricing = pricingForMode(modeOrPricing);
  if (!pricing.supported) return unavailablePrior(rel, pricing);
  if (pricing.pricedMode === PATTERN.AR) return arPrior(rel, pricing);
  return dominantPrior(rel, pricing);
}
